import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Brain } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { pickByLang } from '@/i18n';

const labels: Record<string, Record<string, string>> = {
  ko: { has: '진행 중인 6주 학습 플랜이 있어요', resume: '플랜 이어보기', none: '아직 학습 방향을 못 정했나요?', start: '30초 진단으로 맞춤 플랜 받기' },
  en: { has: 'You have a 6-week study plan in progress', resume: 'Resume your plan', none: 'Not sure where to start?', start: 'Get a plan from a 30-sec diagnosis' },
  ja: { has: '進行中の6週間プランがあります', resume: 'プランを続ける', none: '何から始めるか迷っていますか？', start: '30秒診断で自分専用プランを取得' },
  'zh-CN': { has: '您有一个进行中的6周学习计划', resume: '继续计划', none: '不知道从哪开始？', start: '30秒测评获取专属计划' },
  'zh-TW': { has: '您有一個進行中的6週學習計畫', resume: '繼續計畫', none: '不知道從哪開始？', start: '30秒測評取得專屬計畫' },
  vi: { has: 'Bạn có kế hoạch học 6 tuần đang thực hiện', resume: 'Tiếp tục kế hoạch', none: 'Chưa biết bắt đầu từ đâu?', start: 'Nhận kế hoạch qua đánh giá 30 giây' },
  th: { has: 'คุณมีแผนเรียน 6 สัปดาห์ที่กำลังทำอยู่', resume: 'ทำแผนต่อ', none: 'ไม่รู้จะเริ่มตรงไหน?', start: 'รับแผนจากการประเมิน 30 วินาที' },
  id: { has: 'Anda punya rencana belajar 6 minggu yang berjalan', resume: 'Lanjutkan rencana', none: 'Bingung mulai dari mana?', start: 'Dapatkan rencana dari diagnosa 30 detik' },
  ms: { has: 'Anda ada pelan belajar 6 minggu sedang berjalan', resume: 'Sambung pelan', none: 'Tak pasti nak mula di mana?', start: 'Dapatkan pelan dari diagnosa 30 saat' },
  my: { has: '၆ ပတ်ကြာ လေ့လာမှုအစီအစဉ် ရှိနေပါသည်', resume: 'အစီအစဉ် ဆက်လုပ်ရန်', none: 'ဘယ်ကနေ စရမလဲ မသေချာဘူးလား?', start: 'စက္ကန့် ၃၀ စစ်ဆေးမှုဖြင့် အစီအစဉ်ရယူပါ' },
  es: { has: 'Tienes un plan de estudio de 6 semanas en curso', resume: 'Retomar tu plan', none: '¿No sabes por dónde empezar?', start: 'Obtén un plan con un diagnóstico de 30 s' },
  'pt-BR': { has: 'Você tem um plano de estudo de 6 semanas em andamento', resume: 'Retomar seu plano', none: 'Não sabe por onde começar?', start: 'Receba um plano com diagnóstico de 30 s' },
  fr: { has: 'Vous avez un plan d\'étude de 6 semaines en cours', resume: 'Reprendre votre plan', none: 'Vous ne savez pas par où commencer ?', start: 'Obtenez un plan via un diagnostic de 30 s' },
  de: { has: 'Du hast einen laufenden 6-Wochen-Lernplan', resume: 'Plan fortsetzen', none: 'Nicht sicher, wo du anfangen sollst?', start: 'Plan aus 30-Sek-Diagnose erhalten' },
  ar: { has: 'لديك خطة دراسية لمدة 6 أسابيع قيد التنفيذ', resume: 'استئناف خطتك', none: 'لا تعرف من أين تبدأ؟', start: 'احصل على خطة عبر تشخيص 30 ثانية' },
};

export default function StudyPlanResumeCard() {
  const { lang, localePath } = useI18n();
  const l = pickByLang(labels, lang, 'en');
  const [hasPlan, setHasPlan] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('power-toefl-study-plan');
      setHasPlan(!!(raw && JSON.parse(raw)?.plan));
    } catch { /* ignore */ }
  }, []);

  return (
    <section className="max-w-4xl mx-auto px-4 my-6">
      <Link
        to={localePath('/diagnose')}
        className="flex items-center justify-between gap-4 rounded-2xl border border-primary/25 bg-primary/5 px-5 py-4 hover:border-primary/50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          {hasPlan ? <Sparkles className="w-6 h-6 text-primary shrink-0" /> : <Brain className="w-6 h-6 text-primary shrink-0" />}
          <p className="text-sm md:text-base font-semibold text-foreground truncate">
            {hasPlan ? l.has : l.none}
          </p>
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-bold text-primary shrink-0">
          {hasPlan ? l.resume : l.start} <ArrowRight className="w-4 h-4" />
        </span>
      </Link>
    </section>
  );
}
